import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, Tags, GripVertical } from "lucide-react";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { TICKET_CATEGORY, TICKET_TYPES } from "@/lib/tickets";

export default function TicketTitlesDialog({ open, onClose }) {
  const { lang } = useLang();
  const de = lang === "de";
  const confirm = useConfirm();
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: "", category: "sonstiges", ticket_type: "versand" });
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/ticket-titles").then((r) => setTitles(r.data || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(() => { if (open) load(); }, [open]);
  if (!open) return null;

  const add = async () => {
    if (!form.label.trim()) { toast.error(de ? "Titel erforderlich" : "Title required"); return; }
    setSaving(true);
    try { await api.post("/ticket-titles", form); setForm({ label: "", category: "sonstiges", ticket_type: "versand" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (t) => { try { await api.patch(`/ticket-titles/${t.id}`, { active: !t.active }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (t) => {
    if (!(await confirm({ title: de ? "Titel löschen?" : "Delete title?", message: t.label, confirmLabel: de ? "Löschen" : "Delete", danger: true }))) return;
    try { await api.delete(`/ticket-titles/${t.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const inp = "bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-amber-500/50";

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="ticket-titles-dialog" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[calc(100vw-2rem)] max-w-xl max-h-[88vh] overflow-y-auto bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900"><Tags className="w-5 h-5 text-amber-600" /> {de ? "Vordefinierte Titel" : "Predefined titles"}</h3>
          <button data-testid="titles-close" onClick={onClose} className="text-gray-600 hover:text-gray-900"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">{de ? "Diese Titel erscheinen zur Auswahl bei der Ticket-Erstellung. Kategorie/Typ werden mit vorbelegt." : "These titles appear when creating tickets and prefill category/type."}</p>

        <div className="bg-white/[0.03] border border-gray-200 rounded-xl p-3 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
            <input data-testid="title-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={de ? "Neuer Titel" : "New title"} className={inp} />
            <select data-testid="title-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inp}>
              {TICKET_CATEGORY.map((c) => <option key={c.id} value={c.id} className="bg-gray-50">{de ? c.de : c.en}</option>)}
            </select>
            <select data-testid="title-type-select" value={form.ticket_type} onChange={(e) => setForm({ ...form, ticket_type: e.target.value })} className={inp}>
              {TICKET_TYPES.map((t) => <option key={t.id} value={t.id} className="bg-gray-50">{de ? t.de : t.en}</option>)}
            </select>
            <button data-testid="title-add-btn" onClick={add} disabled={saving} className="flex items-center gap-1.5 bg-amber-500/90 hover:bg-amber-500 text-black font-semibold rounded-lg px-3 py-2 text-sm disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {de ? "Hinzufügen" : "Add"}</button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
        ) : (
          <div className="space-y-1.5" data-testid="titles-list">
            {titles.map((t) => {
              const cat = TICKET_CATEGORY.find((c) => c.id === t.category);
              const typ = TICKET_TYPES.find((x) => x.id === t.ticket_type);
              return (
                <div key={t.id} data-testid={`title-row-${t.id}`} className={`flex items-center justify-between gap-2 bg-white/[0.02] border border-gray-100 rounded-lg px-3 py-2 ${!t.active ? "opacity-50" : ""}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    <GripVertical className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                    <span className="text-sm text-gray-900 truncate">{t.label}</span>
                    {cat && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600">{de ? cat.de : cat.en}</span>}
                    {typ && <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${typ.cls}`}>{de ? typ.de : typ.en}</span>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`title-toggle-${t.id}`} onClick={() => toggle(t)} className="text-[11px] px-2 py-1 rounded border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100">{t.active ? (de ? "Aktiv" : "Active") : (de ? "Inaktiv" : "Inactive")}</button>
                    <button data-testid={`title-delete-${t.id}`} onClick={() => del(t)} className="text-gray-600 hover:text-red-700 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
